async function submitNotifyIntent({ shopDomain, variantId, productId, email, acceptsMarketing, locale }) {
  const response = await fetch('https://app.stoqapp.com/api/v1/intents.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Shop-Domain': shopDomain,
    },
    body: JSON.stringify({
      intent: {
        shopify_variant_id: variantId,
        shopify_product_id: productId,
        channel: 'email',
        quantity: 1,
        source: 'api',
      },
      customer: {
        email,
        accepts_marketing: acceptsMarketing,
        locale,
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Stoq intent request failed with status ${response.status}`);
  }

  const data = await response.json();
  const errors = data?.errors;
  const hasErrors = Array.isArray(errors) ? errors.length > 0 : Boolean(errors && Object.keys(errors).length > 0);

  if (hasErrors) {
    throw new Error(`Stoq intent request returned errors: ${JSON.stringify(errors)}`);
  }

  return data;
}

if (!customElements.get('notify-me-drawer')) {
  class NotifyMeDrawer extends HTMLElement {
    constructor() {
      super();
      this.bindOnSubmit = this.onSubmit.bind(this);
      this.bindUpdateSubmitState = this.updateSubmitState.bind(this);
      this.bindClose = this.close.bind(this);
    }

    connectedCallback() {
      this.drawer = this.querySelector('theme-drawer');
      this.form = this.querySelector('[data-notify-form]');
      this.emailInput = this.querySelector('[data-notify-email]');
      this.emailError = this.querySelector('[data-notify-email-error]');
      this.consentInput = this.querySelector('[data-notify-consent]');
      this.submitButton = this.querySelector('.notify-me__submit');
      this.formError = this.querySelector('[data-notify-form-error]');
      this.closeButton = this.querySelector('[data-notify-close]');
      this.thumbnailImage = this.querySelector('[data-notify-image]');
      this.titleElement = this.querySelector('[data-notify-title]');
      this.variantElement = this.querySelector('[data-notify-variant]');
      this.sizeElement = this.querySelector('[data-notify-size]');
      this.toastIconTemplate = document.getElementById('NotifyMeToastIcon');

      this.form?.addEventListener('submit', this.bindOnSubmit);
      this.emailInput?.addEventListener('input', this.bindUpdateSubmitState);
      this.consentInput?.addEventListener('change', this.bindUpdateSubmitState);
      this.closeButton?.addEventListener('click', this.bindClose);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.bindOnSubmit);
      this.emailInput?.removeEventListener('input', this.bindUpdateSubmitState);
      this.consentInput?.removeEventListener('change', this.bindUpdateSubmitState);
      this.closeButton?.removeEventListener('click', this.bindClose);
    }

    open(data) {
      this.data = data || {};
      this.populate(this.data);
      this.form?.reset();
      this.setEmailError(false);
      this.setFormError(null);
      this.updateSubmitState();
      this.drawer?.open();
    }

    close() {
      this.drawer?.close();
    }

    populate(data) {
      if (this.titleElement) this.titleElement.textContent = data.title || '';

      if (this.variantElement) {
        this.variantElement.textContent = data.variant || '';
        this.variantElement.hidden = !data.variant;
      }

      if (this.sizeElement) {
        if (data.size) {
          const template = theme.locales?.product?.notify?.sizeLabel || '';
          this.sizeElement.textContent = template.replace('[size]', data.size);
          this.sizeElement.hidden = false;
        } else {
          this.sizeElement.textContent = '';
          this.sizeElement.hidden = true;
        }
      }

      if (this.thumbnailImage) {
        if (data.image) {
          this.thumbnailImage.src = this.getSizedImageUrl(data.image, 160);
          this.thumbnailImage.alt = data.title || '';
          this.thumbnailImage.hidden = false;
        } else {
          this.thumbnailImage.removeAttribute('src');
          this.thumbnailImage.hidden = true;
        }
      }
    }

    getSizedImageUrl(src, width) {
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', width);
        return url.toString();
      } catch (error) {
        return src;
      }
    }

    updateSubmitState() {
      const emailValid = this.emailInput?.checkValidity();
      const consentChecked = this.consentInput?.checked;
      const isValid = Boolean(emailValid && consentChecked);

      this.setEmailError(this.emailInput?.value !== '' && !emailValid);
      this.setSubmitEnabled(isValid);

      return isValid;
    }

    setSubmitEnabled(enabled) {
      if (!this.submitButton) return;
      if (enabled) {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute('aria-disabled');
      } else {
        this.submitButton.setAttribute('disabled', '');
        this.submitButton.setAttribute('aria-disabled', 'true');
      }
    }

    setEmailError(show) {
      this.emailError?.toggleAttribute('hidden', !show);
    }

    setFormError(message) {
      if (!this.formError) return;
      this.formError.textContent = message || '';
      this.formError.toggleAttribute('hidden', !message);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.updateSubmitState()) return;

      this.setFormError(null);
      this.setSubmitEnabled(false);
      this.submitButton?.classList.add('loading');

      theme.pubsub?.publish(theme.pubsub.events.FORM_SUBMIT, { form: 'notify-me' });

      try {
        await submitNotifyIntent({
          shopDomain: this.dataset.shopDomain,
          variantId: this.data?.variantId,
          productId: this.data?.productId,
          email: this.emailInput.value.trim(),
          acceptsMarketing: this.consentInput.checked,
          locale: this.dataset.locale,
        });

        theme.pubsub?.publish(theme.pubsub.events.FORM_SUCCESS, { form: 'notify-me' });
        this.close();
        this.showConfirmationToast();
      } catch (error) {
        theme.pubsub?.publish(theme.pubsub.events.FORM_ERROR, { form: 'notify-me', error });
        const message = error instanceof TypeError
          ? theme.locales?.form?.error?.network
          : theme.locales?.form?.error?.generic;
        this.setFormError(message);
      } finally {
        this.submitButton?.classList.remove('loading');
        this.updateSubmitState();
      }
    }

    showConfirmationToast() {
      const icon = this.toastIconTemplate?.content.firstElementChild?.cloneNode(true) || null;
      theme.toast?.show({
        type: 'notify',
        message: theme.locales?.product?.notify?.confirmed || '',
        icon,
      });
    }
  }

  customElements.define('notify-me-drawer', NotifyMeDrawer);
}
